/**
 * Guardrail Validation System
 * 
 * Provides pre-built guardrails and validation utilities for
 * controlling agent transitions and preventing invalid flow jumps.
 */

import { AgentContext, Guardrail, EntryGuardrails, SupportedLanguage } from './types';

// =============================================================================
// COMMON GUARDRAILS - Reusable across multiple agents
// =============================================================================

/**
 * Guardrail: Language must be detected
 */
export const languageDetectedGuardrail: Guardrail = {
  id: 'language_detected',
  description: 'User language preference must be set',
  validate: (context: AgentContext) => {
    if (!context.transitionData.language) {
      return 'Language has not been detected yet. Cannot proceed without language preference.';
    }
    return true;
  },
};

/**
 * Guardrail: User data must be available
 */
export const userDataRequiredGuardrail: Guardrail = {
  id: 'user_data_required',
  description: 'User must be logged in with name and mobile number',
  validate: (context: AgentContext) => {
    const userData = context.transitionData.userData;
    if (!userData || !userData.username || !userData.mobileNumber) {
      return 'User data (name and mobile) is required for this operation.';
    }
    return true;
  },
};

/**
 * Guardrail: Voice gender must be set
 */
export const voiceGenderSetGuardrail: Guardrail = {
  id: 'voice_gender_set',
  description: 'Voice gender preference must be configured',
  validate: (context: AgentContext) => {
    if (!context.transitionData.voiceGender) {
      return 'Voice gender has not been set.';
    }
    return true;
  },
};

// =============================================================================
// EMERGENCY TRIAGE FLOW GUARDRAILS
// =============================================================================

/**
 * Guardrail: An incident summary must exist to enter triage
 */
export const incidentSummaryGuardrail: Guardrail = {
  id: 'incident_summary_required',
  description: 'An incident summary must be provided to enter triage',
  validate: (context: AgentContext) => {
    if (!context.transitionData.incidentSummary) {
      return 'No incident summary provided. Cannot start triage without context.';
    }
    return true;
  },
};

/**
 * Guardrail: Recommended agencies must be set to enter reporting
 */
export const recommendedAgenciesGuardrail: Guardrail = {
  id: 'recommended_agencies_required',
  description: 'Recommended agencies must be determined before reporting consent',
  validate: (context: AgentContext) => {
    const agencies = context.transitionData.recommendedAgencies;
    if (!agencies || agencies.length === 0) {
      return 'No agencies recommended. Cannot proceed to reporting without agency list.';
    }
    return true;
  },
};

// =============================================================================
// VALIDATION UTILITIES
// =============================================================================

/**
 * Validate all required guardrails for entry
 * @returns Array of error messages, empty if all pass
 */
export function validateEntryGuardrails(
  guardrails: EntryGuardrails,
  context: AgentContext
): string[] {
  const errors: string[] = [];
  
  for (const guardrail of guardrails.required) {
    const result = guardrail.validate(context);
    if (result !== true) {
      errors.push(typeof result === 'string' ? result : `Guardrail ${guardrail.id} failed`);
    }
  }
  
  // Log optional guardrail warnings (but don't block)
  if (guardrails.optional) {
    for (const guardrail of guardrails.optional) {
      const result = guardrail.validate(context);
      if (result !== true) {
        console.warn(`[GUARDRAIL WARNING] ${guardrail.id}: ${result}`);
      }
    }
  }
  
  return errors;
}

/**
 * Find valid transition from exit guardrails
 * @returns Target agent ID and any errors, or null if no valid transition
 */
export function findValidTransition(
  exitGuardrails: Map<string, { target: { agentId: string }; guardrails: Guardrail[] }>,
  transitionName: string,
  context: AgentContext
): { targetAgentId: string; errors: string[] } | null {
  const transition = exitGuardrails.get(transitionName);
  
  if (!transition) {
    return null;
  }
  
  const errors: string[] = [];
  for (const guardrail of transition.guardrails) {
    const result = guardrail.validate(context);
    if (result !== true) {
      errors.push(typeof result === 'string' ? result : `Guardrail ${guardrail.id} failed`);
    }
  }
  
  return {
    targetAgentId: transition.target.agentId,
    errors,
  };
}

/**
 * Create a simple "no requirements" entry guardrails object
 */
export function noEntryRequirements(): EntryGuardrails {
  return {
    required: [],
    optional: [],
  };
}

/**
 * Create entry guardrails with just language requirement
 */
export function requireLanguage(): EntryGuardrails {
  return {
    required: [languageDetectedGuardrail],
    optional: [],
  };
}

/**
 * Create entry guardrails requiring language and user data
 */
export function requireLanguageAndUser(): EntryGuardrails {
  return {
    required: [languageDetectedGuardrail, userDataRequiredGuardrail],
    optional: [],
  };
}

/**
 * Create a custom guardrail
 */
export function createGuardrail(
  id: string,
  description: string,
  validate: (context: AgentContext) => boolean | string
): Guardrail {
  return { id, description, validate };
}

/**
 * Combine multiple guardrails into an AND condition
 */
export function allOf(...guardrails: Guardrail[]): Guardrail {
  return {
    id: `all_of_${guardrails.map(g => g.id).join('_')}`,
    description: `All of: ${guardrails.map(g => g.description).join(', ')}`,
    validate: (context: AgentContext) => {
      for (const guardrail of guardrails) {
        const result = guardrail.validate(context);
        if (result !== true) {
          return result;
        }
      }
      return true;
    },
  };
}

/**
 * Combine multiple guardrails into an OR condition
 */
export function anyOf(...guardrails: Guardrail[]): Guardrail {
  return {
    id: `any_of_${guardrails.map(g => g.id).join('_')}`,
    description: `Any of: ${guardrails.map(g => g.description).join(', ')}`,
    validate: (context: AgentContext) => {
      const errors: string[] = [];
      for (const guardrail of guardrails) {
        const result = guardrail.validate(context);
        if (result === true) {
          return true;
        }
        errors.push(typeof result === 'string' ? result : `Guardrail ${guardrail.id} failed`);
      }
      return errors.join(' OR ');
    },
  };
}

